// useLogin (contract 06 §9) — wraps AuthLoginFlow, owns the reactive form/pending/error, and
// does navigation on the flow's typed outcome. Invalid credentials are ONE inline state; system
// failures are visibly distinct. `error` carries an i18n KEY (never raw text). No Remember control.
import { ref, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { AuthLoginFlow } from '../flows/AuthLoginFlow'
import { toPostLoginLocation } from '../flows/postLoginNavigation'
import { AuthError } from '../models/auth.error'

export interface UseLogin {
  email: Ref<string>
  password: Ref<string>
  pending: Ref<boolean>
  error: Ref<string | null>
  submit: () => Promise<void>
  loginWithGoogle: () => void
  maybeShowVerifiedAlert: () => string | null
}

export function useLogin(): UseLogin {
  const router = useRouter()
  const route = useRoute()
  const flow = new AuthLoginFlow()

  const email = ref('')
  const password = ref('')
  const pending = ref(false)
  const error = ref<string | null>(null)

  async function submit(): Promise<void> {
    pending.value = true
    error.value = null
    try {
      // Empty `?next=` is not a target — treat it as absent so it doesn't shadow redirectTarget.
      const rawNext = route.query.next
      const nextTarget = typeof rawNext === 'string' && rawNext !== '' ? rawNext : null
      const outcome = await flow.execute(
        { email: email.value, password: password.value },
        nextTarget,
      )
      if (outcome.next === 'mfa') {
        await router.push({ name: 'MfaCode', query: { email: outcome.email } })
      } else {
        await router.push(toPostLoginLocation(outcome.target))
      }
    } catch (e) {
      const authError = AuthError.from(e)
      error.value =
        authError.code === 'invalidCredentials'
          ? 'auth.login.invalidCredentials'
          : 'auth.login.systemError'
    } finally {
      pending.value = false
    }
  }

  return {
    email,
    password,
    pending,
    error,
    submit,
    loginWithGoogle: () => {
      flow.startGoogleLoginRedirect()
    },
    maybeShowVerifiedAlert: () => flow.maybeShowVerifiedAlert(route.query),
  }
}
